<!-- workshops.component.html -->
<div class="container mx-auto p-8">
  <div class="mb-8 mt-24">
    <h1 class="text-3xl font-bold text-gray-800 mb-2">Gestión de Talleres</h1>
    <p class="text-gray-600">Administra talleres y genera etiquetas PDF</p>
  </div>

  <!-- Loading Spinner -->
  <div *ngIf="loading" class="flex justify-center items-center py-8">
    <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>

  <!-- Error Message -->
  <div *ngIf="error" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
    {{ error }}
  </div>

  <!-- Search and Add Section -->
  <div class="bg-white rounded-lg shadow-lg p-6 mb-6">
    <div class="flex flex-col md:flex-row gap-4 items-center justify-between">
      <!-- Search -->
      <div class="flex-1 max-w-md">
        <div class="relative">
          <input
            type="text"
            [(ngModel)]="searchTerm"
            (input)="onSearch()"
            placeholder="Buscar talleres..."
            class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
          <i class="fas fa-search absolute left-3 top-3 text-gray-400"></i>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex gap-3">
        <!-- Multiple Selection Toggle -->
        <button
          (click)="toggleMultipleSelection()"
          [class]="multipleSelectionMode ? 'bg-orange-600 hover:bg-orange-700' : 'bg-gray-600 hover:bg-gray-700'"
          class="text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center gap-2"
        >
          <i class="fas fa-check-square"></i>
          {{ multipleSelectionMode ? 'Cancelar Selección' : 'Selección Múltiple' }}
        </button>

        <!-- Generate Multiple PDFs Button (only visible in selection mode) -->
        <button
          *ngIf="multipleSelectionMode && selectedWorkshops.length > 0"
          (click)="openMultipleLabelsModal()"
          class="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center gap-2"
        >
          <i class="fas fa-file-pdf"></i>
          Generar PDFs ({{ selectedWorkshops.length }})
        </button>

        <!-- Add Button -->
        <button
          (click)="openAddModal()"
          class="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center gap-2"
        >
          <i class="fas fa-plus"></i>
          Agregar Taller
        </button>
      </div>
    </div>
  </div>

  <!-- Workshops List -->
  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    <div 
      *ngFor="let workshop of filteredWorkshops; trackBy: trackByWorkshopId"
      [class]="'bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300' + (multipleSelectionMode && isWorkshopSelected(workshop) ? ' ring-2 ring-orange-500' : '')"
    >
      <div class="p-6">
        <!-- Workshop Header -->
        <div class="flex items-start justify-between mb-4">
          <div class="flex items-start gap-3 flex-1">
            <!-- Checkbox for multiple selection -->
            <input 
              *ngIf="multipleSelectionMode"
              type="checkbox"
              [checked]="isWorkshopSelected(workshop)"
              (change)="toggleWorkshopSelection(workshop)"
              class="mt-1 h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
            >
            <h3 class="text-xl font-semibold text-gray-800 truncate">{{ workshop.name }}</h3>
          </div>
          <div class="flex gap-2" *ngIf="!multipleSelectionMode">
            <button
              (click)="openEditModal(workshop)"
              class="text-blue-600 hover:text-blue-800 p-1"
              title="Editar"
            >
              <i class="fas fa-edit"></i>
            </button>
            <button
              (click)="openDeleteModal(workshop)"
              class="text-red-600 hover:text-red-800 p-1"
              title="Eliminar"
            >
              <i class="fas fa-trash"></i>
            </button>
          </div>
        </div>

        <!-- Workshop Details -->
        <div class="space-y-2 mb-4">
          <div class="flex items-center text-blue-600 mb-2">
            <i class="fas fa-building w-4 mr-2"></i>
            <span class="text-sm font-medium">{{ workshop.company }}</span>
          </div>
          <div *ngIf="workshop.address" class="flex items-center text-gray-600">
            <i class="fas fa-map-marker-alt w-4 mr-2"></i>
            <span class="text-sm">{{ workshop.address }}</span>
          </div>
          <div *ngIf="workshop.phoneNumber" class="flex items-center text-gray-600">
            <i class="fas fa-phone w-4 mr-2"></i>
            <span class="text-sm">{{ workshop.phoneNumber }}</span>
          </div>
          <div *ngIf="workshop.department || workshop.city" class="flex items-center text-gray-600">
            <i class="fas fa-map w-4 mr-2"></i>
            <span class="text-sm">{{ workshop.department }}{{ workshop.department && workshop.city ? ', ' : '' }}{{ workshop.city }}</span>
          </div>
          <div class="flex items-center text-gray-500">
            <i class="fas fa-calendar w-4 mr-2"></i>
            <span class="text-xs">Creado: {{ workshop.createdAt | date:'dd/MM/yyyy' }}</span>
          </div>
        </div>

        <!-- Actions -->
        <div class="pt-4 border-t border-gray-200">
          <button
            (click)="openLabelsModal(workshop)"
            class="w-full bg-orange-600 hover:bg-orange-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
          >
            <i class="fas fa-tags"></i>
            Generar Etiquetas
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Empty State -->
  <div *ngIf="!loading && filteredWorkshops.length === 0" class="text-center py-12">
    <i class="fas fa-tools text-6xl text-gray-300 mb-4"></i>
    <h3 class="text-xl font-medium text-gray-500 mb-2">No hay talleres</h3>
    <p class="text-gray-400 mb-4">
      {{ searchTerm ? 'No se encontraron talleres con el término de búsqueda.' : 'Comienza agregando tu primer taller.' }}
    </p>
    <button
      *ngIf="!searchTerm"
      (click)="openAddModal()"
      class="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
    >
      Agregar Primer Taller
    </button>
  </div>
</div>

<!-- Add Workshop Modal -->
<div *ngIf="showAddModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
  <div class="bg-white rounded-lg p-6 w-full max-w-md mx-4">
    <div class="flex justify-between items-center mb-4">
      <h2 class="text-xl font-semibold">Agregar Taller</h2>
      <button (click)="closeAddModal()" class="text-gray-500 hover:text-gray-700">
        <i class="fas fa-times"></i>
      </button>
    </div>
    
    <form (ngSubmit)="saveWorkshop()">
      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Nombre *</label>
          <input
            type="text"
            [(ngModel)]="newWorkshop.name"
            name="name"
            required
            maxlength="255"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Nombre del taller"
          >
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Dirección</label>
          <input
            type="text"
            [(ngModel)]="newWorkshop.address"
            name="address"
            maxlength="500"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Dirección del taller"
          >
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Teléfono</label>
          <input
            type="text"
            [(ngModel)]="newWorkshop.phoneNumber"
            name="phoneNumber"
            maxlength="50"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Número de teléfono"
          >
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Departamento</label>
          <input
            type="text"
            [(ngModel)]="newWorkshop.department"
            name="department"
            maxlength="100"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Departamento"
          >
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Ciudad</label>
          <input
            type="text"
            [(ngModel)]="newWorkshop.city"
            name="city"
            maxlength="100"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Ciudad"
          >
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Empresa *</label>
          <select
            [(ngModel)]="newWorkshop.company"
            name="company"
            required
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="AUTOCOMERCIAL">Autocomercial del Norte</option>
            <option value="SERIMPORT">Ser-Import</option>
          </select>
        </div>

        <div class="flex items-center gap-2">
          <input
            id="newWorkshopAlertOrder"
            type="checkbox"
            [(ngModel)]="newWorkshop.alertOrder"
            name="alertOrder"
            class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          >
          <label for="newWorkshopAlertOrder" class="text-sm font-medium text-gray-700">
            Alerta Orden
          </label>
        </div>
      </div>
      
      <div class="flex gap-3 mt-6">
        <button
          type="button"
          (click)="closeAddModal()"
          class="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancelar
        </button>
        <button
          type="submit"
          [disabled]="loading || !newWorkshop.name.trim()"
          class="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {{ loading ? 'Guardando...' : 'Guardar' }}
        </button>
      </div>
    </form>
  </div>
</div>

<!-- Edit Workshop Modal -->
<div *ngIf="showEditModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
  <div class="bg-white rounded-lg p-6 w-full max-w-md mx-4">
    <div class="flex justify-between items-center mb-4">
      <h2 class="text-xl font-semibold">Editar Taller</h2>
      <button (click)="closeEditModal()" class="text-gray-500 hover:text-gray-700">
        <i class="fas fa-times"></i>
      </button>
    </div>
    
    <form (ngSubmit)="updateWorkshop()">
      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Nombre *</label>
          <input
            type="text"
            [(ngModel)]="editWorkshop.name"
            name="name"
            required
            maxlength="255"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Nombre del taller"
          >
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Dirección</label>
          <input
            type="text"
            [(ngModel)]="editWorkshop.address"
            name="address"
            maxlength="500"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Dirección del taller"
          >
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Teléfono</label>
          <input
            type="text"
            [(ngModel)]="editWorkshop.phoneNumber"
            name="phoneNumber"
            maxlength="50"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Número de teléfono"
          >
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Departamento</label>
          <input
            type="text"
            [(ngModel)]="editWorkshop.department"
            name="editDepartment"
            maxlength="100"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Departamento"
          >
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Ciudad</label>
          <input
            type="text"
            [(ngModel)]="editWorkshop.city"
            name="editCity"
            maxlength="100"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Ciudad"
          >
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Empresa *</label>
          <select
            [(ngModel)]="editWorkshop.company"
            name="editCompany"
            required
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="AUTOCOMERCIAL">Autocomercial del Norte</option>
            <option value="SERIMPORT">Ser-Import</option>
          </select>
        </div>

        <div class="flex items-center gap-2">
          <input
            id="editWorkshopAlertOrder"
            type="checkbox"
            [(ngModel)]="editWorkshop.alertOrder"
            name="editAlertOrder"
            class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          >
          <label for="editWorkshopAlertOrder" class="text-sm font-medium text-gray-700">
            Alerta Orden
          </label>
        </div>
      </div>
      
      <div class="flex gap-3 mt-6">
        <button
          type="button"
          (click)="closeEditModal()"
          class="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancelar
        </button>
        <button
          type="submit"
          [disabled]="loading || !editWorkshop.name.trim()"
          class="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {{ loading ? 'Actualizando...' : 'Actualizar' }}
        </button>
      </div>
    </form>
  </div>
</div>

<!-- Delete Workshop Modal -->
<div *ngIf="showDeleteModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
  <div class="bg-white rounded-lg p-6 w-full max-w-md mx-4">
    <div class="flex justify-between items-center mb-4">
      <h2 class="text-xl font-semibold text-red-600">Eliminar Taller</h2>
      <button (click)="closeDeleteModal()" class="text-gray-500 hover:text-gray-700">
        <i class="fas fa-times"></i>
      </button>
    </div>
    
    <div class="mb-6">
      <p class="text-gray-700">
        ¿Estás seguro de que deseas eliminar el taller <strong>{{ selectedWorkshop?.name }}</strong>?
      </p>
      <p class="text-sm text-red-600 mt-2">Esta acción no se puede deshacer.</p>
    </div>
    
    <div class="flex gap-3">
      <button
        type="button"
        (click)="closeDeleteModal()"
        class="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
      >
        Cancelar
      </button>
      <button
        (click)="deleteWorkshop()"
        [disabled]="loading"
        class="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {{ loading ? 'Eliminando...' : 'Eliminar' }}
      </button>
    </div>
  </div>
</div>

<!-- Generate Labels Modal -->
<div *ngIf="showLabelsModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
  <div class="bg-white rounded-lg p-6 w-full max-w-md mx-4">
    <div class="flex justify-between items-center mb-4">
      <h2 class="text-xl font-semibold">Generar Etiquetas</h2>
      <button (click)="closeLabelsModal()" class="text-gray-500 hover:text-gray-700">
        <i class="fas fa-times"></i>
      </button>
    </div>
    
    <div class="mb-6">
      <p class="text-gray-700 mb-4">
        Generar etiquetas para: <strong>{{ selectedWorkshop?.name }}</strong>
      </p>
      
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Cantidad de etiquetas</label>
        <input
          type="number"
          [(ngModel)]="labelQuantity"
          min="1"
          max="100"
          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          placeholder="Cantidad (1-100)"
        >
        <p class="text-xs text-gray-500 mt-1">Máximo 100 etiquetas por vez</p>
      </div>
    </div>
    
    <div class="flex gap-3">
      <button
        type="button"
        (click)="closeLabelsModal()"
        class="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
      >
        Cancelar
      </button>
      <button
        (click)="generateLabels()"
        [disabled]="loading || labelQuantity < 1 || labelQuantity > 100"
        class="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {{ loading ? 'Generando...' : 'Generar PDF' }}
      </button>
    </div>
  </div>
</div>

<!-- Generate Multiple Labels Modal -->
<div *ngIf="showMultipleLabelsModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
  <div class="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-96 overflow-y-auto">
    <div class="flex justify-between items-center mb-4">
      <h2 class="text-xl font-semibold">Generar Etiquetas Múltiples</h2>
      <button (click)="closeMultipleLabelsModal()" class="text-gray-500 hover:text-gray-700">
        <i class="fas fa-times"></i>
      </button>
    </div>
    
    <div class="mb-6">
      <p class="text-gray-700 mb-4">
        Se generarán etiquetas para <strong>{{ selectedWorkshops.length }}</strong> taller(es) seleccionado(s):
      </p>
      
      <!-- Lista de talleres seleccionados con cantidades individuales -->
      <div class="bg-gray-50 rounded-lg p-4 mb-4 max-h-48 overflow-y-auto">
        <div class="space-y-3">
          <div *ngFor="let workshop of selectedWorkshops" class="flex items-center justify-between p-2 bg-white rounded border">
            <div class="flex items-center flex-1">
              <i class="fas fa-check-circle text-green-500 mr-3"></i>
              <div class="flex-1">
                <span class="font-medium text-sm">{{ workshop.name }}</span>
                <span class="text-gray-500 text-xs ml-2">({{ workshop.company }})</span>
              </div>
            </div>
            <div class="flex items-center gap-2">
              <label class="text-xs text-gray-600">Cantidad:</label>
              <input
                type="number"
                [value]="workshopQuantities.get(workshop.workshopId!) || multipleLabelQuantity"
                (input)="updateWorkshopQuantity(workshop.workshopId!, $event)"
                min="1"
                max="100"
                class="w-16 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-orange-500 focus:border-transparent"
                placeholder="1-100"
              >
            </div>
          </div>
        </div>
      </div>
      
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
        <div class="flex items-center gap-2">
          <i class="fas fa-info-circle text-blue-500"></i>
          <span class="text-sm text-blue-700">
            <strong>Cantidad global:</strong> Establece la cantidad para todos los talleres que no tengan cantidad específica
          </span>
        </div>
        <div class="mt-2">
          <label class="block text-sm font-medium text-gray-700 mb-1">Cantidad por defecto</label>
          <input
            type="number"
            [(ngModel)]="multipleLabelQuantity"
            min="1"
            max="100"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            placeholder="Cantidad (1-100)"
          >
          <p class="text-xs text-gray-500 mt-1">Esta cantidad se aplicará a talleres sin cantidad específica</p>
        </div>
      </div>
    </div>
    
    <div class="flex gap-3">
      <button
        type="button"
        (click)="closeMultipleLabelsModal()"
        class="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
      >
        Cancelar
      </button>
      <button
        (click)="generateMultipleLabels()"
        [disabled]="loading || selectedWorkshops.length === 0"
        class="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {{ loading ? 'Generando...' : 'Generar PDF con Cantidades Individuales' }}
      </button>
    </div>
  </div>
</div> 